import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useQuery } from '@tanstack/react-query';
import SupabaseConnectionService from '@/services/SupabaseConnectionService';
import { toast } from 'sonner';
import { Save, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import GraphConnectionService from '@/services/connection/GraphConnectionService';

const IntegrationSettings = () => {
  const [clientId, setClientId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scopes, setScopes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{success: boolean, message: string} | null>(null);

  const { data: graphSettings, isLoading } = useQuery({
    queryKey: ['graphSettings'],
    queryFn: async () => {
      return await SupabaseConnectionService.getGraphSettings();
    }
  });

  useEffect(() => {
    if (graphSettings) {
      setClientId(graphSettings.client_id || '');
      setTenantId(graphSettings.tenant_id || '');
      setClientSecret(graphSettings.client_secret || '');
      setScopes(graphSettings.scopes || 'https://graph.microsoft.com/.default');
    }
  }, [graphSettings]);

  const handleSaveGraphSettings = async () => {
    if (!clientId || !tenantId) {
      toast.error('Missing required fields', {
        description: 'Client ID and Tenant ID are required'
      });
      return;
    }

    setIsSaving(true);
    try {
      await SupabaseConnectionService.saveGraphSettings({
        client_id: clientId,
        tenant_id: tenantId,
        client_secret: clientSecret,
        certificate: '',
        certificate_password: '',
        scopes: scopes || 'https://graph.microsoft.com/.default',
        endpoint: 'https://graph.microsoft.com/v1.0'
      });

      toast.success('Microsoft Graph settings saved', {
        description: 'Your connection settings have been updated'
      });
      setTestResult(null);
    } catch (error) {
      console.error('Error saving Graph settings:', error);
      toast.error('Failed to save settings', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!clientId || !tenantId || !clientSecret) {
      toast.error('Missing required fields', {
        description: 'Client ID, Tenant ID and Client Secret are required for testing'
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);
    
    try {
      GraphConnectionService.configure({
        clientId: clientId,
        tenantId: tenantId,
        clientSecret: clientSecret,
        scopes: scopes || 'https://graph.microsoft.com/.default',
        endpoint: 'https://graph.microsoft.com/v1.0'
      });
      
      const result = await GraphConnectionService.testConnection();
      
      if (result.success) {
        setTestResult({
          success: true,
          message: 'Connection successful! Your Microsoft Graph API credentials are valid.'
        });
        toast.success('Connection test successful', {
          description: 'Your Microsoft Graph API credentials are valid'
        });
      } else {
        setTestResult({
          success: false,
          message: `Connection failed: ${result.error}`
        });
        toast.error('Connection test failed', {
          description: result.error
        });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({
        success: false,
        message: `Error testing connection: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      toast.error('Connection test error', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg md:text-xl mobile-break-words">Integration Settings</CardTitle>
        <CardDescription className="text-sm mobile-break-words">
          Configure third-party integrations and services
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 border rounded-md space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h3 className="text-base font-medium">Microsoft Graph API</h3>
              <p className="text-sm text-muted-foreground">
                Configure credentials for Microsoft Graph API connections
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client-id" className="text-sm">Application (Client) ID</Label>
                <Input 
                  id="client-id" 
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Enter your Azure AD app registration client ID" 
                />
                <p className="text-xs text-muted-foreground">
                  From your app registration in the Azure Portal
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tenant-id" className="text-sm">Directory (Tenant) ID</Label>
                <Input 
                  id="tenant-id" 
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="Enter your Azure AD tenant ID" 
                />
                <p className="text-xs text-muted-foreground">
                  From your Azure Active Directory overview
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="client-secret" className="text-sm">Client Secret</Label>
                <Input 
                  id="client-secret" 
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="Enter your app Client Secret" 
                />
                <p className="text-xs text-muted-foreground">
                  From your app's certificates & secrets section
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scopes" className="text-sm">API Permissions (Scopes)</Label>
                <Input 
                  id="scopes" 
                  value={scopes}
                  onChange={(e) => setScopes(e.target.value)}
                  placeholder="https://graph.microsoft.com/.default" 
                />
                <p className="text-xs text-muted-foreground">
                  Default is recommended for most scenarios
                </p>
              </div>
              
              {testResult && (
                <div className={`p-3 rounded-md ${testResult.success ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                  <div className="flex items-center">
                    {testResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 mr-2" />
                    )}
                    <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {testResult.message}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={isTesting || !clientId || !tenantId || !clientSecret}
                >
                  {isTesting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>
                
                <Button 
                  onClick={handleSaveGraphSettings}
                  disabled={isSaving || !clientId || !tenantId}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Graph Settings
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
        
        <Separator />
        
        <div className="flex items-center justify-between p-4 border rounded-md">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary h-5 w-5">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
              <h3 className="text-sm md:text-base font-medium mobile-break-words">Azure Active Directory</h3>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground mobile-break-words">Connect to manage users and groups</p>
          </div>
          <Button variant="outline" size="sm" className="whitespace-nowrap">Configure</Button>
        </div>
        
        <div className="flex items-center justify-between p-4 border rounded-md">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary h-5 w-5">
                <path d="M15 2H9a1 1 0 0 0-1 1v2c0 .6.4 1 1 1h6c.6 0 1-.4 1-1V3c0-.6-.4-1-1-1Z"></path>
                <path d="M20 16V9a1 1 0 0 0-1-1h-1"></path>
                <path d="M6 8H5a1 1 0 0 0-1 1v7"></path>
                <rect x="2" y="16" width="20" height="6" rx="1"></rect>
              </svg>
              <h3 className="text-sm md:text-base font-medium mobile-break-words">Power BI</h3>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground mobile-break-words">Connect to view governance reports</p>
          </div>
          <Button variant="outline" size="sm" className="whitespace-nowrap">Configure</Button>
        </div>
        
        <div className="flex items-center justify-between p-4 border rounded-md">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary h-5 w-5">
                <circle cx="12" cy="12" r="4"></circle>
                <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"></path>
              </svg>
              <h3 className="text-sm md:text-base font-medium mobile-break-words">Microsoft Teams</h3>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground mobile-break-words">Connect to manage teams and channels</p>
          </div>
          <Button variant="outline" size="sm" className="whitespace-nowrap">Configure</Button>
        </div>
        
        <Separator />
        
        <div className="space-y-2">
          <h3 className="text-base md:text-lg font-medium mobile-break-words">Webhooks</h3>
          <p className="text-xs md:text-sm text-muted-foreground mobile-break-words">Configure webhooks for event notifications</p>
          
          <div className="space-y-2 mt-4">
            <Label htmlFor="webhook-url">Webhook URL</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input id="webhook-url" placeholder="https://your-service.com/webhook" className="flex-1" />
              <Button className="whitespace-nowrap">Add Webhook</Button>
            </div>
          </div>
          
          <div className="mt-4 space-y-2">
            <Label>Active Webhooks</Label>
            <div className="rounded-md border overflow-hidden">
              <div className="p-3 bg-muted/30 flex items-center justify-between">
                <div className="truncate max-w-[200px] sm:max-w-none">
                  <p className="text-xs md:text-sm font-medium mobile-break-words">https://example.com/webhook1</p>
                  <p className="text-xs text-muted-foreground mobile-break-words">Added on: 2023-06-05</p>
                </div>
                <Button variant="ghost" size="sm" className="text-destructive">Remove</Button>
              </div>
              <div className="p-3 flex items-center justify-between">
                <div className="truncate max-w-[200px] sm:max-w-none">
                  <p className="text-xs md:text-sm font-medium mobile-break-words">https://example.com/webhook2</p>
                  <p className="text-xs text-muted-foreground mobile-break-words">Added on: 2023-07-10</p>
                </div>
                <Button variant="ghost" size="sm" className="text-destructive">Remove</Button>
              </div>
            </div>
          </div>
        </div>
        
        <Separator />
        
        <div className="space-y-2">
          <h3 className="text-base md:text-lg font-medium mobile-break-words">API Keys</h3>
          <p className="text-xs md:text-sm text-muted-foreground mobile-break-words">Manage API keys for external services</p>
          
          <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-end">
            <Button>Generate New API Key</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default IntegrationSettings;
